import React, { useState, useEffect, useRef } from 'react';
import { NodeViewContent, NodeViewWrapper } from '@tiptap/react';
import { ResizableBox } from 'react-resizable';
import 'react-resizable/css/styles.css';

import Image from '@tiptap/extension-image';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { Box, Center, Flex, Icon, Tooltip } from '@chakra-ui/react';
import { Upload, TrashCan } from '@/assets/icons/attachements';

function useOutsideDetector(ref, onOutsideClick = () => {}) {
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target))
        onOutsideClick(event);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [ref, onOutsideClick]);
}

export const DetectOutsideClick = ({ onOutsideClick, children }) => {
  const wrapperRef = useRef(null);
  useOutsideDetector(wrapperRef, onOutsideClick);

  return (
    <div ref={wrapperRef} style={{ display: 'inline' }}>
      {children}
    </div>
  );
};

export const ImageResizeComponent = ({
  node,
  updateAttributes,
  extension,
  ...rest
}) => {
  const [startingWidth, setStartingWidth] = useState(-1);
  const [startingHeight, setStartingHeight] = useState(-1);
  const [isResizing, setIsResizing] = useState(false);
  const [isHover, setIsHover] = useState(false);

  const minLength = 20;
  const maxLength = 700;
  const minConstraints =
    startingWidth === -1
      ? [minLength, minLength]
      : startingWidth > startingHeight
      ? [(startingWidth / startingHeight) * minLength, minLength]
      : [minLength, (startingHeight / startingWidth) * minLength];
  const maxConstraints =
    startingWidth === -1
      ? [minLength, minLength]
      : startingWidth > startingHeight
      ? [maxLength, maxLength / (startingWidth / startingHeight)]
      : [maxLength, maxLength / (startingWidth / startingHeight)];

  const onImgLoad = (event) => {
    const img = event.target;
    setStartingWidth(img.offsetWidth);
    setStartingHeight(img.offsetHeight);
    updateAttributes({ width: img.offsetWidth, height: img.offsetHeight });
  };

  return (
    <NodeViewWrapper as="span" onClick={() => setIsResizing(true)}>
      <DetectOutsideClick onOutsideClick={() => setIsResizing(false)}>
        <ResizableBox
          width={startingWidth}
          height={startingHeight}
          style={{ zIndex: 1 }}
          onResize={(e, data) => {
            const { width, height } = data.size;
            updateAttributes({ width, height });
          }}
          draggableOpts={{ grid: [1, 1] }}
          lockAspectRatio={true}
          handle={(direction, ref) =>
            isResizing ? (
              <span
                className={`custom-handle custom-handle-${direction}`}
                ref={ref}
              />
            ) : (
              <div />
            )
          }
          handleSize={[8, 8]}
          resizeHandles={['sw', 'se', 'nw', 'ne', 'n', 's', 'e', 'w']}
          minConstraints={minConstraints}
          maxConstraints={maxConstraints}
        >
          <Box
            data-test-id="email-editor-inline-image"
            position="absolute"
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
          >
            <Flex
              gap="7px"
              pos="absolute"
              justifyContent="center"
              alignItems="center"
              w="100%"
              h="100%"
              zIndex="2"
            >
              {isHover && (
                <>
                  <Tooltip
                    label="Replace"
                    arrowSize={8}
                    hasArrow
                    borderRadius="4px"
                    shouldWrapChildren
                    bg="#161616"
                  >
                    <Center
                      w={
                        node?.attrs?.width < 120 || node?.attrs?.height < 120
                          ? '32px'
                          : '56px'
                      }
                      h={
                        node?.attrs?.width < 120 || node?.attrs?.height < 120
                          ? '32px'
                          : '56px'
                      }
                      bg="rgba(22, 22, 22, 0.5)"
                      borderRadius="full"
                      cursor="pointer"
                      onClick={() => {
                        rest?.deleteNode();
                        document?.getElementById('editor-image-input')?.click();
                      }}
                      data-test-id="email-editor-inline-image-reload"
                    >
                      <Icon
                        as={Upload}
                        w={
                          node?.attrs?.width < 120 || node?.attrs?.height < 120
                            ? '18px'
                            : '26px'
                        }
                        h={
                          node?.attrs?.width < 120 || node?.attrs?.height < 120
                            ? '18px'
                            : '26px'
                        }
                      />
                    </Center>
                  </Tooltip>
                  <Tooltip
                    label="Delete"
                    arrowSize={8}
                    hasArrow
                    borderRadius="4px"
                    shouldWrapChildren
                    bg="#161616"
                  >
                    <Center
                      w={
                        node?.attrs?.width < 120 || node?.attrs?.height < 120
                          ? '32px'
                          : '56px'
                      }
                      h={
                        node?.attrs?.width < 120 || node?.attrs?.height < 120
                          ? '32px'
                          : '56px'
                      }
                      bg="rgba(22, 22, 22, 0.5)"
                      borderRadius="full"
                      cursor="pointer"
                      onClick={() => {
                        rest?.deleteNode();
                      }}
                      data-test-id="email-editor-inline-image-delete"
                    >
                      <Icon
                        as={TrashCan}
                        w={
                          node?.attrs?.width < 120 || node?.attrs?.height < 120
                            ? '18px'
                            : '26px'
                        }
                        h={
                          node?.attrs?.width < 120 || node?.attrs?.height < 120
                            ? '18px'
                            : '26px'
                        }
                      />
                    </Center>
                  </Tooltip>
                </>
              )}
            </Flex>
            <NodeViewContent
              as="img"
              {...node.attrs}
              onLoad={onImgLoad}
              style={{
                display: 'block',
                outline: isResizing ? '1px solid #0043CE' : undefined,
                opacity: isHover ? 0.8 : 1,
              }}
            />
          </Box>
        </ResizableBox>
      </DetectOutsideClick>
    </NodeViewWrapper>
  );
};

export const SizedImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: { default: null },
      height: { default: null },
      'data-og-width': { default: null },
      'data-og-height': { default: null },
    };
  },
  addCommands() {
    return {
      setImage:
        (options) =>
        ({ tr, commands }) => {
          if (options?.['data-og-width'] > 500) {
            options.width = 500;
          }
          return commands.insertContent({ type: this.name, attrs: options });
        },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(ImageResizeComponent);
  },
});
